/*
 * Project: bellcloud
 * File: packages/protocols/mini/fn_cal_bump.ts
 * Author: SWAN
 * Created: 2025-01-27
 * Description: ClassID.CALIBRATION (0x04)에 대한 FunctionID별 파라미터 정의
 */
import type { FunctionParameter } from '../fns';

export const params_cal_bump: Record<number, FunctionParameter[]> = {
  0x01: [
    {
      name: 'target_gas_conc',
      type: 'as32[]',
      bytes: 20,
      comment: 'target gas concentration : x1000 with user conc. unit',
      format: 'array_conc',
    },
    {
      name: 'sensor_type',
      type: 'au8[]',
      bytes: 5,
      comment: 'sensor type, 255 when sensor is not selected',
      format: 'enum_array_sensors',
    },
    {
      name: 'calibration_type',
      type: 'u8',
      bytes: 1,
      comment:
        '0:user zero, 1:user span, 2:factory zero, 3:factory span, 4:user fresh air, 5:factory fresh air',
      format: 'enum_cal_type',
    },
  ],
  0x02: [
    {
      name: 'start_pass_fail',
      type: 'u8',
      bytes: 1,
      comment: '0: fail, 1: success',
      format: 'number',
    },
    { name: 'timeout', type: 'u16', bytes: 2, comment: 'max timeout', format: 'number' },
    {
      name: 'sensor_type',
      type: 'au8[]',
      bytes: 5,
      comment: 'actual sensor number used or 255 if not operated',
      format: 'enum_sensors',
    },
  ],
  0x03: [
    {
      name: 'calibration_type',
      type: 'u8',
      bytes: 1,
      comment: '0~5, same as above',
      format: 'enum_cal_type',
    },
  ],
  0x04: [
    {
      name: 'last_calibration_date',
      type: 'au32[]',
      bytes: 20,
      comment: 'days since 2023.01.01',
      format: 'array_date_from_basedate',
    },
    {
      name: 'last_bump_date',
      type: 'au32[]',
      bytes: 20,
      comment: 'days since 2023.01.01',
      format: 'array_date_from_basedate',
    },
    {
      name: 'gas_concentration',
      type: 'as32[]',
      bytes: 20,
      comment: 'x1000 value',
      format: 'array_number',
    },
    {
      name: 'calibration_due_date',
      type: 'as16[]',
      bytes: 10,
      comment: '0~365 or negative (overdue)',
      format: 'array_date_with_sign',
    },
    {
      name: 'bump_due_date',
      type: 'as16[]',
      bytes: 10,
      comment: '0~365 or negative (overdue)',
      format: 'array_date_with_sign',
    },
    {
      name: 'calibration_status',
      type: 'au8[]',
      bytes: 5,
      comment: '0:ing, 1:pass, 2:fail, 3:abort, 4:skip',
      format: 'enum_cal_status',
    },
    {
      name: 'decimal_point',
      type: 'au8[]',
      bytes: 5,
      comment: 'used for EOL SW',
      format: 'array_number',
    },
    {
      name: 'conc_unit',
      type: 'au8[]',
      bytes: 5,
      comment: 'used for EOL SW',
      format: 'enum_array_gas_conc_unit',
    },
    {
      name: 'sensor_type',
      type: 'au8[]',
      bytes: 5,
      comment: 'sensor number or 255 if not installed',
      format: 'enum_sensors',
    },
    {
      name: 'calibration_type',
      type: 'u8',
      bytes: 1,
      comment: '0~5, same as above',
      format: 'enum_cal_type',
    },
    {
      name: 'min_calibration_due_date',
      type: 's16',
      bytes: 2,
      comment: 'min due date incl. CO2',
      format: 'date_with_sign',
    },
    {
      name: 'target_span_gas_conc',
      type: 'as32[]',
      bytes: 20,
      comment: 'target conc for span',
      format: 'array_conc',
    },
  ],
  0x05: [
    {
      name: 'target_gas_conc',
      type: 'as32[]',
      bytes: 20,
      comment: 'target gas conc',
      format: 'array_conc',
    },
    {
      name: 'sensor_type',
      type: 'au8[]',
      bytes: 5,
      comment: 'sensor type or 255',
      format: 'enum_sensors',
    },
  ],
  0x06: [
    {
      name: 'start_pass_fail',
      type: 'u8',
      bytes: 1,
      comment: '0: fail, 1: success',
      format: 'number',
    },
    { name: 'timeout', type: 'u16', bytes: 2, comment: 'default 60~120', format: 'number' },
    {
      name: 'sensor_type',
      type: 'au8[]',
      bytes: 5,
      comment: 'actual sensor or 255',
      format: 'enum_sensors',
    },
  ],
  0x07: [], // Request Bump Test Status - no parameters
  0x08: [
    {
      name: 'last_span_calibration_date',
      type: 'au32[]',
      bytes: 20,
      comment: 'days since 2023.01.01',
      format: 'array_date_from_basedate',
    },
    {
      name: 'last_bump_date',
      type: 'au32[]',
      bytes: 20,
      comment: 'days since 2023.01.01',
      format: 'array_date_from_basedate',
    },
    {
      name: 'calibration_due_date',
      type: 'as16[]',
      bytes: 10,
      comment: '0~365 or negative',
      format: 'array_date_with_sign',
    },
    {
      name: 'bump_due_date',
      type: 'as16[]',
      bytes: 10,
      comment: '0~365 or negative',
      format: 'array_date_with_sign',
    },
    {
      name: 'bump_test_status',
      type: 'au8[]',
      bytes: 5,
      comment: '0:ing, 1:pass, 2:fail, 3:abort',
      format: 'enum_array_bump_test_status',
    },
    {
      name: 'sensor_type',
      type: 'au8[]',
      bytes: 5,
      comment: 'sensor type',
      format: 'enum_sensors',
    },
    {
      name: 'min_bump_due_date',
      type: 's16',
      bytes: 2,
      comment: 'minimum bump due',
      format: 'date_with_sign',
    },
  ],
  0x09: [], // Request Abort Span Calibration - no parameters
  0x0a: [{ name: 'result', type: 'u8', bytes: 1, comment: '0: Fail, 1: Pass', format: 'bool' }],
  0x0b: [], // Request Abort Bump Test - no parameters
  0x0c: [{ name: 'result', type: 'u8', bytes: 1, comment: '0: Fail, 1: Pass', format: 'bool' }],
};
